/**
 * AWS RUM (Real User Monitoring) bootstrap.
 *
 * Captures JS errors, performance, and http telemetry from real browsers so
 * client-side failures — like the 2026-07-25 status-0 CORS burst, which
 * never reached API Gateway and was therefore invisible in every server-side
 * log — show up in CloudWatch RUM.
 *
 * Design rules (ported from the OculusMind.AI implementation):
 * - Telemetry must never perturb the flow it observes: init and record are
 *   wrapped in try/catch and only ever console.warn.
 * - Disabled when the config still has deploy placeholders, and for
 *   visitors signaling Global Privacy Control.
 */
import { AwsRum } from "aws-rum-web";
import { buildRumConfig, getRumStage } from "./rumConfig";
const APP_VERSION = "1.0.0";
let client;
const readGpc = () => {
    const nav = navigator;
    return nav.globalPrivacyControl === true;
};
// Exported for tests.
export const rumAllowed = (config, gpc) => {
    if (!config.appMonitorId || config.appMonitorId.startsWith("REPLACED_")) {
        return false;
    }
    if (gpc) {
        return false;
    }
    return true;
};
export const initRum = () => {
    try {
        if (client) {
            return;
        }
        const stage = getRumStage(window.location.href, window.location.hostname);
        const config = buildRumConfig(stage);
        if (!rumAllowed(config, readGpc())) {
            return;
        }
        const awsRumConfig = {
            sessionSampleRate: 1,
            identityPoolId: config.identityPoolId,
            guestRoleArn: config.guestRoleArn,
            endpoint: `https://dataplane.rum.${config.region}.amazonaws.com`,
            telemetries: ["errors", "performance", "http"],
            allowCookies: true,
            enableXRay: false,
        };
        client = new AwsRum(config.appMonitorId, APP_VERSION, config.region, awsRumConfig);
    }
    catch (err) {
        console.warn("[rum] init failed (telemetry disabled): " + err);
    }
};
// Custom event helper for high-signal failures the generic telemetries miss
// (e.g., API retry exhaustion). Attributes must stay PII-free.
export const recordRumEvent = (eventType, attributes) => {
    try {
        client === null || client === void 0 ? void 0 : client.recordEvent(eventType, attributes);
    }
    catch (_a) {
        // Telemetry must never perturb the flow it observes.
    }
};
